import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  useGetPlayerMe,
  getGetPlayerMeQueryKey,
  getListMyRegistrationsQueryKey,
  usePlayerLogin,
  usePlayerSignup,
  usePlayerLogout,
  type PlayerMe,
} from "@workspace/api-client-react";
import { setSentryUser } from "@/lib/sentry";

export type CurrentPlayer = PlayerMe;

export function useCurrentPlayer() {
  const qc = useQueryClient();
  const meQueryKey = getGetPlayerMeQueryKey();

  const { data, isLoading, isFetching } = useGetPlayerMe({
    query: {
      queryKey: meQueryKey,
      retry: false,
      staleTime: 60_000,
      // Treat 401 as "not signed in" — react-query's default throws and
      // would put the page in an error state, so swallow it via select.
      throwOnError: false,
    },
  });

  const setMe = (p: PlayerMe | null) => {
    qc.setQueryData(meQueryKey, p ?? undefined);
  };

  const refresh = () => qc.invalidateQueries({ queryKey: meQueryKey });

  // Mirror the resolved player into Sentry so client-side errors are tagged
  // with who hit them. Cleared when the session goes away.
  useEffect(() => {
    if (data) {
      setSentryUser({
        id: data.id,
        email: data.email,
        name: data.name,
        role: "player",
      });
    } else if (!isLoading && !isFetching) {
      setSentryUser(null);
    }
  }, [data, isLoading, isFetching]);

  return {
    player: data ?? null,
    isLoggedIn: !!data,
    isLoading: isLoading || isFetching,
    setPlayer: setMe,
    refresh,
  };
}

export function usePlayerAuthActions() {
  const qc = useQueryClient();
  const meQueryKey = getGetPlayerMeQueryKey();

  const onAuthSuccess = (p: PlayerMe) => {
    qc.setQueryData(meQueryKey, p);
    setSentryUser({ id: p.id, email: p.email, name: p.name, role: "player" });
  };
  const onLogoutSuccess = () => {
    qc.setQueryData(meQueryKey, undefined);
    qc.removeQueries({ queryKey: meQueryKey });
    // Drop any per-player cached data so it can't leak into the
    // anonymous UI (e.g. RSVP'd badges on tournament cards).
    qc.removeQueries({ queryKey: getListMyRegistrationsQueryKey() });
    setSentryUser(null);
  };

  const login = usePlayerLogin({ mutation: { onSuccess: onAuthSuccess } });
  const signup = usePlayerSignup({ mutation: { onSuccess: onAuthSuccess } });
  const logout = usePlayerLogout({ mutation: { onSuccess: onLogoutSuccess } });

  return { login, signup, logout };
}
